import { useState } from 'react';
import StatusBadge from '../../../components/StatusBadge';
import { Episode } from '../../../types';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

interface EpisodePageHeaderProps {
  episode: Episode;
  projectName: string;
  polling: boolean;
  t: Translate;
  children?: React.ReactNode;
  headerDuration?: React.ReactNode;
  headerActions?: React.ReactNode;
}

export default function EpisodePageHeader({
  episode,
  projectName,
  polling,
  t,
  children,
  headerDuration,
  headerActions,
}: EpisodePageHeaderProps) {
  const statusLabel = episode.status === 'pending' && episode.promptGenerationStatus !== 'generating'
    ? t('episodeInfo.readyToGenerate')
    : undefined;

  const [copied, setCopied] = useState(false);
  const copyProjectId = () => {
    navigator.clipboard?.writeText(episode.projectId)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => { /* 剪贴板不可用时静默 */ });
  };

  return (
    <>
      <div className="card theme-left-border" style={{ borderRadius: '10px', '--_theme-color': 'var(--theme-storyboard)', '--_theme-color-end': '#3b82f6' } as React.CSSProperties}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <StatusBadge status={episode.status} showSpinner={polling} label={statusLabel} />
          <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'baseline', gap: '0.5rem' }}>
            {projectName && <span style={{ color: 'var(--text)', fontWeight: 700 }}>{projectName}</span>}
            {projectName && <span style={{ color: 'var(--text-2)', fontWeight: 400 }}>/</span>}
            <span style={{ color: 'var(--theme-storyboard)', fontWeight: 400 }}>{t('episodeInfo.episodeN', { n: episode.index })}</span>
            {headerDuration}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.72rem', fontWeight: 400, color: 'var(--text-3)' }}>
              <span>{t('episodeInfo.projectIdLabel')}</span>
              <code style={{ fontFamily: 'monospace', color: 'var(--text-2)' }}>{episode.projectId}</code>
              <button
                type="button"
                onClick={copyProjectId}
                title={copied ? t('episodeInfo.projectIdCopied') : t('episodeInfo.copyProjectId')}
                aria-label={t('episodeInfo.copyProjectId')}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 'none', padding: '2px', cursor: 'pointer',
                  color: copied ? 'var(--success)' : 'var(--text-3)', lineHeight: 0,
                }}
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
              </button>
            </span>
          </h2>
          {headerActions && <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>{headerActions}</div>}
        </div>
        {episode.errorMsg && (
          <p className="error-text" style={{ marginTop: '0.75rem' }}>{t('episodeInfo.error', { msg: episode.errorMsg })}</p>
        )}
        {children}
      </div>
    </>
  );
}
